import React from 'react';
import Section from '../components/Section';
import { Briefcase } from 'lucide-react';

const Experience = () => {
    const experiences = [
        {
            title: "AI Automation Extern",
            company: "Outamation (via Extern), Michigan, USA",
            period: "Feb 2026 - PRESENT",
            description: [
                "AI Architecture Research: Evaluated the application of Large Language Models (LLMs) and Natural Language Processing (NLP) for extracting high-accuracy data from unstructured legal documents.",
                "Computer Vision & OCR: Explored the integration of Optical Character Recognition (OCR) to automate the identification of missing documentation and improve data validation precision.",
                "Workflow Transformation: Conducted a deep-dive analysis of manual mortgage document processing to identify high-friction bottlenecks suitable for AI intervention.",
                "Strategic Analysis: Documented the real-world obstacles AI models face in regulated industries, providing a roadmap for testing and refining automation techniques in the next phase of the program."
            ]
        },
        {
            title: "AI Fullstack Developer",
            company: "UPHP, Michigan, USA",
            period: "June 2024 - Jan 2026",
            description: [
                "Design, build, test, and support multi-tier web applications and REST APIs using Java (Spring Boot) and C#/ASP.NET connected to normalized SQL database layers.",
                "Execute full system lifecycle duties including system detail design, multi-tier module development, automated testing, defect troubleshooting, and user/system documentation.",
                "Implement scalable data preparation, ETL, and inference pipelines in Python utilizing PyTorch, TensorFlow, and Hugging Face libraries.",
                "Participate in IT system implementation projects, production maintenance, and support services for enterprise and government-regulated platforms, maintaining 99.9% environment availability.",
                "Work closely with engineering team members to develop and test application modules according to strict functional and technical system specifications.",
                "Assist System Analysts (SAs) and Business Analysts (BAs) with requirement analysis, workflow modeling, and translation of business logic into detailed technical design specs.",
                "Maintain high quality standards under tight project deadlines, delivering clear technical communications across cross-functional delivery teams."
            ]
        },
        {
            title: "Fullstack Software Engineer",
            company: "Avishkar Tech Solutions, India",
            period: "Feb 2022 - Dec 2023",
            description: [
                "Developed web and desktop interfaces using JavaScript, Java, Python, HTML, CSS, and SQL database layers.",
                "Engineered REST APIs and integrated third-party machine learning APIs into web application platforms.",
                "Constructed server-side business logic and responsive user interface views following detailed system design specifications.",
                "Managed source control repositories via Git, participating in code reviews, automated unit testing, and continuous delivery releases.",
                "Utilized Git version control to manage code repositories and collaborate effectively in Agile team environments.",
                "Participated in system testing, bug remediation, technical documentation writing, and release support for client applications."
            ]
        },
        {
            title: "RPA Intern",
            company: "AICTE-Eduskills RPA Blue Prism Virtual Internship",
            period: "April 2021 - June 2021",
            description: [
                "RPA Development: Engineered Robotic Process Automation (RPA) solutions using Blue Prism to automate repetitive workflows, significantly reducing operational errors and processing time.",
                "Bot Lifecycle Management: Led the end-to-end development of bots—from process analysis and configuration to rigorous testing and deployment—ensuring high reliability and scalability.",
                "Compliance & Optimization: Focused on process industrialization, ensuring all automation scripts adhered to strict data privacy regulations and configuration management standards."
            ]
        }
    ];

    return (
        <Section id="experience" className="bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">Professional Experience</h2>
                <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                    My journey in software development and research.
                </p>
            </div>

            <div className="max-w-4xl mx-auto">
                {experiences.map((exp, index) => (
                    <div key={index} className="relative pl-8 md:pl-0 mb-12 last:mb-0 group">
                        {/* Timeline Line */}
                        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 -translate-x-1/2 group-last:bottom-auto group-last:h-full"></div>

                        <div className={`md:flex items-start justify-between ${index % 2 === 0 ? 'flex-row-reverse' : ''}`}>
                            {/* Timeline Dot */}
                            <div className="absolute left-0 md:left-1/2 w-4 h-4 rounded-full bg-blue-600 dark:bg-blue-500 border-4 border-white dark:border-gray-800 shadow-sm -translate-x-[5px] md:-translate-x-1/2 mt-1.5 z-10"></div>

                            {/* Date (Desktop) */}
                            <div className={`hidden md:block w-5/12 ${index % 2 === 0 ? 'text-left' : 'text-right'} pt-1`}>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full text-sm">
                                    {exp.period}
                                </span>
                            </div>

                            {/* Content Card */}
                            <div className="md:w-5/12 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-700">
                                <div className="md:hidden mb-2">
                                    <span className="text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full text-xs">
                                        {exp.period}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{exp.title}</h3>
                                <h4 className="text-lg text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <Briefcase size={16} className="mr-2 text-gray-400" />
                                    {exp.company}
                                </h4>
                                <ul className="space-y-2">
                                    {exp.description.map((item, i) => (
                                        <li key={i} className="text-gray-600 dark:text-gray-400 text-sm flex items-start">
                                            <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
};

export default Experience;
